/* eslint-disable consistent-return */
/* eslint-disable import/no-extraneous-dependencies */
import * as React from "react";
import { styled, alpha } from "@mui/material/styles";

import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ArticleIcon from "@mui/icons-material/Article";
import { AccountTree, Class } from "@mui/icons-material";
import FolderRounded from "@mui/icons-material/FolderRounded";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import { useTreeItem } from "@mui/x-tree-view/useTreeItem";
import { TreeItemIconContainer, TreeItemLabel } from "@mui/x-tree-view/TreeItem";
import { TreeItemIcon } from "@mui/x-tree-view/TreeItemIcon";
import { TreeItemProvider } from "@mui/x-tree-view/TreeItemProvider";
import { useTreeItemModel } from "@mui/x-tree-view/hooks";

const TreeItemRoot = styled("li")(({ theme }) => ({
	listStyle: "none",
	margin: 0,
	padding: 0,
	outline: 0,
	color: theme.palette.grey[400],
	...theme.applyStyles("light", {
		color: theme.palette.grey[800]
	})
}));

const TreeItemContent = styled("div")(({ theme }) => ({
	padding: theme.spacing(0.5),
	paddingRight: theme.spacing(1),
	paddingLeft: `calc(${theme.spacing(1)} + var(--TreeView-itemChildrenIndentation) * var(--TreeView-itemDepth))`,
	width: "100%",
	boxSizing: "border-box", // prevent width + padding to overflow
	position: "relative",
	display: "flex",
	alignItems: "center",
	gap: theme.spacing(1),
	cursor: "pointer",
	WebkitTapHighlightColor: "transparent",
	borderRadius: theme.spacing(0.7),
	fontWeight: 500,
	"&[data-expanded]:not([data-focused], [data-selected]) .labelIcon": {
		color: theme.palette.info.main,
		...theme.applyStyles("light", {
			color: theme.palette.info.main
		}),
		"&::before": {
			content: '""',
			display: "block",
			position: "absolute",
			left: "16px",
			top: "44px",
			height: "calc(100% - 48px)",
			width: "1.5px",
			backgroundColor: theme.palette.grey[700],
			...theme.applyStyles("light", {
				backgroundColor: theme.palette.grey[300]
			})
		}
	},
	[`&[data-focused], &[data-selected]`]: {
		backgroundColor: theme.palette.info.dark,
		color: theme.palette.info.main,
		...theme.applyStyles("light", {
			backgroundColor: "#bbdefb"
		})
	},
	"&:not([data-focused], [data-selected]):hover": {
		backgroundColor: alpha("#bbdefb", 0.1),
		color: theme.palette.info.main,
		...theme.applyStyles("light", {
			color: theme.palette.info.main
		})
	}
}));

const CustomCollapse = styled(Collapse)({
	padding: 0
});

function TransitionComponent(props) {
	return <CustomCollapse {...props} />;
}

const TreeItemLabelText = styled(Typography)({
	userSelect: "none"
});

function CustomLabel({ icon: Icon, expandable, children, isFolder, handleOpenMenu, ...other }) {
	return (
		<TreeItemLabel
			{...other}
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				width: "100%"
			}}
		>
			<Box sx={{ display: "flex", alignItems: "center" }}>
				{Icon && (
					<Box
						component={Icon}
						className="labelIcon"
						color="inherit"
						sx={{ mr: 1, fontSize: "1.2rem" }}
					/>
				)}
				<TreeItemLabelText variant="body2">{children}</TreeItemLabelText>
			</Box>

			<IconButton
				size="small"
				onClick={handleOpenMenu}
				sx={{
					padding: 0.5,
					"&:hover": {
						backgroundColor: "rgba(0, 0, 0, 0.04)"
					}
				}}
			>
				<MoreVertIcon fontSize="small" />
			</IconButton>
		</TreeItemLabel>
	);
}

const getIconFromType = (type, format) => {
	switch (type) {
		case "themeCategory":
			return Class;
		case "asset": {
			switch (format) {
				case "theme":
					return AccountTree;
				default:
					return ArticleIcon;
			}
		}
		default:
			return FolderRounded;
	}
};

const CustomTreeItem = React.forwardRef((props, ref) => {
	const {
		id,
		itemId,
		label,
		name,
		disabled,
		children,
		// Additional props passed from FileExplorer
		path,
		//	folder,
		depth,
		onClick,
		onContextMenu,
		onOpenMenu,
		canDeleteFolder,
		canEditFolder,
		canCreateFolder,
		canCreateAsset,
		localState,
		setLocalState,
		// Other props
		...other
	} = props;

	const item = useTreeItemModel(itemId);

	const {
		getContextProviderProps,
		getRootProps,
		getContentProps,
		getIconContainerProps,
		getLabelProps,
		getGroupTransitionProps,
		status
	} = useTreeItem({ id, itemId, children, label, disabled, rootRef: ref });

	const isFolder = item.type === "folder";

	let icon = getIconFromType(item.type, item.format);

	// Memoize click handlers to prevent recreating functions
	const handleClick = React.useCallback(() => {
		onClick(item, path);
	}, [onClick, item.id]);

	const handleContextMenu = React.useCallback(
		e => {
			if (!canCreateAsset) return null;
			onContextMenu(e, item);
		},
		[onContextMenu, id, canCreateAsset]
	);

	const handleOpenMenu = React.useCallback(
		e => {
			e.stopPropagation();
			onOpenMenu(e, item);
		},
		[onOpenMenu, id]
	);

	// Override the icon container props to force expandable state for folders
	const iconContainerProps = getIconContainerProps();
	if (isFolder) {
		iconContainerProps["data-expandable"] = true;
	}

	return (
		<TreeItemProvider {...getContextProviderProps()}>
			<TreeItemRoot {...getRootProps(other)}>
				<TreeItemContent
					{...getContentProps({ onClick: handleClick, onContextMenu: handleContextMenu })}
				>
					<TreeItemIconContainer {...iconContainerProps}>
						<TreeItemIcon status={isFolder ? { ...status, expandable: true } : status} />
					</TreeItemIconContainer>
					<CustomLabel
						{...getLabelProps({
							icon,
							expandable: isFolder,
							isFolder,
							handleOpenMenu
						})}
					/>
				</TreeItemContent>
				<TransitionComponent {...getGroupTransitionProps()} />
			</TreeItemRoot>
		</TreeItemProvider>
	);
});

export default function FileExplorer({
	folders,
	path = [],
	// folder,
	depth,
	onClick,
	onContextMenu,
	onOpenMenu,
	canDeleteFolder,
	canEditFolder,
	canCreateFolder,
	canCreateAsset,
	localState,
	setLocalState,
	expandedItems,
	handleExpandedItemsChange,
	selectedItems,
	handleSelectedItemsChange
}) {
	const getItemLabel = item => {
		return item.name;
	};

	return (
		<RichTreeView
			selectedItems={selectedItems}
			expandedItems={expandedItems}
			onExpandedItemsChange={handleExpandedItemsChange}
			onSelectedItemsChange={handleSelectedItemsChange}
			items={folders}
			slots={{ item: CustomTreeItem }}
			getItemLabel={getItemLabel}
			slotProps={{
				item: {
					path,
					// folder,
					depth,
					onClick,
					onContextMenu,
					onOpenMenu,
					canDeleteFolder,
					canEditFolder,
					canCreateFolder,
					canCreateAsset,
					localState,
					setLocalState
				}
			}}
		/>
	);
}
